import type { ExperienceSearchParams, ExperienceSequence } from "@/lib/types";
import { isShowLocalReferencesEnabled } from "@/lib/experiences/launchFlags";
import type { MarketplaceSearchContext } from "@/lib/experiences/marketplaceSearchContext";
import {
  SERVICE_TYPES_BY_VIEW,
  type ServiceTypeView,
} from "@/components/experiences/marketplace/marketplaceFilters";

// ─── Marketplace Inventory Scale & Discovery V1 ─────────────────────────────
// Pure request-building logic for POST /experiences/search, extracted out of
// experiences.tsx so it's directly unit-testable. Never invents a field —
// every value here comes straight from the context already on screen or
// the user's own filter/sort selections.

export interface MarketplaceFiltersLike {
  categories:   Set<string>;
  duration:     "short" | "medium" | "long" | null;
  timeOfDay:    string | null;
  maxPrice:     number | null;
  includeTipBased: boolean;
  providerOnly: boolean;
  reservable:   boolean;
  sortBy:       "default" | "duration_asc" | "price_asc" | "provider_first";
  /** Full-Width Marketplace Grid & Scalable Provider Filter V1 — canonical
   *  provider keys; empty Set = "all providers". */
  providers:    Set<string>;
  /** Marketplace Service-Type Reachability V1 — see SERVICE_TYPES_BY_VIEW. */
  serviceTypeView: ServiceTypeView;
  /** Sprint: Filter Taxonomy + Dynamic Result Filters V1 — canonical
   *  filter-taxonomy group ids selected as chips; empty Set = none selected. */
  filterTaxonomy: Set<string>;
  /** Hotfix/Sprint: Language Selector Marketplace Wiring V1 — "es-CO" |
   *  "en-US" | "pt-BR", forwarded as-is to market_locale below. */
  marketLocale: string;
}

/** Default page size — task 2's "tamaño inicial recomendado: 24". */
export const MARKETPLACE_SEARCH_PAGE_SIZE = 24;

/**
 * Unified Marketplace Shell + Direct Structured Search V1 — the primary,
 * provider-agnostic request builder. Accepts a MarketplaceSearchContext
 * (never an ExperienceSequence) so a screen that never calls
 * /experiences/sequence at all can still build a correct
 * POST /experiences/search request. See buildMarketplaceSearchParamsFromSequence
 * below for the /experiences/sequence-compatible wrapper routes/experiences.tsx
 * keeps using unchanged.
 */
export function buildMarketplaceSearchParams(
  context: MarketplaceSearchContext,
  filters: MarketplaceFiltersLike,
  opts: {
    cursor?: string | null;
    limit?:  number;
  } = {},
): ExperienceSearchParams {
  const destination = context.cityDisplay || context.city;
  const childrenAges = context.childrenAges ?? [];
  return {
    query:         context.intent?.trim() || destination,
    search_session: context.searchSession,
    city_iata:     context.cityIata,
    city:          destination,
    travel_date:   context.dates?.travelDate ?? undefined,
    travelers:     context.adults,
    has_children:  childrenAges.length > 0,
    children_ages: childrenAges.length > 0 ? childrenAges : undefined,
    categories:    Array.from(filters.categories),
    // Hotfix: Marketplace Semantic Intent Propagation + Scalable Pool
    // Capacity V1, Parte A — the real declared ranking context, always
    // from the context, never derived from filters.categories (a UI
    // filter, not ranking intent) and never derived from free-text intent.
    interests:     context.interests ?? [],
    duration:      filters.duration,
    time_of_day:   filters.timeOfDay,
    max_price:     filters.maxPrice,
    include_tip_based: filters.includeTipBased,
    // Provider-Only Marketplace & Comparison V2 Foundation, Part B #5 — the
    // request itself must ask for provider_only=true in launch mode, never
    // relying solely on the backend's own flag (defense in depth: the
    // backend still enforces this independently either way). filters.
    // providerOnly is preserved as the user's own toggle for the non-launch
    // (RHEA_SHOW_LOCAL_REFERENCES=true) case.
    provider_only: !isShowLocalReferencesEnabled() || filters.providerOnly,
    reservable:    filters.reservable,
    providers:     Array.from(filters.providers),
    service_types: SERVICE_TYPES_BY_VIEW[filters.serviceTypeView],
    filter_taxonomy: Array.from(filters.filterTaxonomy),
    market_locale: filters.marketLocale,
    sort:          filters.sortBy,
    cursor:        opts.cursor ?? undefined,
    limit:         opts.limit ?? MARKETPLACE_SEARCH_PAGE_SIZE,
    // Sprint: Destination Marketplace Identity vs Nearest Airport
    // Separation V1, Parte E — forwarded as-is; all undefined when the
    // context wasn't built with them (old deep links, sequence-based
    // flows), which the backend treats identically to a request that
    // never sends these fields at all. nearest_airport_iata only travels
    // alongside a real destination_id/marketplace_search_name — cityIata
    // is the picker's own selectable code (see DestinationPicker's
    // destinationCandidateToSuggestion doc), so echoing it here ONLY when
    // there's a genuine new-contract identity keeps this field honest as
    // a logistics-only diagnostic, never conflated with a plain legacy
    // city_iata search.
    destination_id:            context.destinationId,
    destination_display_name:  context.cityDisplay || undefined,
    marketplace_search_name:   context.marketplaceSearchName,
    nearest_airport_iata:      (context.destinationId || context.marketplaceSearchName)
      ? context.cityIata
      : undefined,
    destination_identity_kind: context.destinationIdentityKind,
  };
}

/**
 * ExperienceSequence-compatible wrapper — byte-for-byte the same behavior
 * this module had before Unified Marketplace Shell + Direct Structured
 * Search V1 (never delegates to the context-based buildMarketplaceSearchParams
 * above, so routes/experiences.tsx's existing behavior/tests can never drift
 * from a change made for the new context-based path, or vice versa).
 */
export function buildMarketplaceSearchParamsFromSequence(
  sequence: Pick<ExperienceSequence, "city" | "city_iata" | "city_display" | "marketplace_search_session" | "interests">,
  filters:  MarketplaceFiltersLike,
  opts: {
    cursor?:       string | null;
    limit?:        number;
    query?:        string;
    travelDate?:   string | null;
    travelers?:    number;
    hasChildren?:  boolean;
    childrenAges?: number[];
  } = {},
): ExperienceSearchParams {
  const destination = sequence.city_display || sequence.city;
  return {
    query:         opts.query?.trim() || destination,
    search_session: sequence.marketplace_search_session,
    city_iata:     sequence.city_iata,
    city:          destination,
    travel_date:   opts.travelDate ?? undefined,
    travelers:     opts.travelers,
    has_children:  opts.hasChildren,
    children_ages: opts.childrenAges,
    categories:    Array.from(filters.categories),
    // Hotfix: Marketplace Semantic Intent Propagation + Scalable Pool
    // Capacity V1, Parte A — the real declared ranking context, always
    // from the sequence, never derived from filters.categories (a UI
    // filter, not ranking intent).
    interests:     sequence.interests ?? [],
    duration:      filters.duration,
    time_of_day:   filters.timeOfDay,
    max_price:     filters.maxPrice,
    include_tip_based: filters.includeTipBased,
    // Provider-Only Marketplace & Comparison V2 Foundation, Part B #5 — the
    // request itself must ask for provider_only=true in launch mode, never
    // relying solely on the backend's own flag (defense in depth: the
    // backend still enforces this independently either way). filters.
    // providerOnly is preserved as the user's own toggle for the non-launch
    // (RHEA_SHOW_LOCAL_REFERENCES=true) case.
    provider_only: !isShowLocalReferencesEnabled() || filters.providerOnly,
    reservable:    filters.reservable,
    providers:     Array.from(filters.providers),
    service_types: SERVICE_TYPES_BY_VIEW[filters.serviceTypeView],
    filter_taxonomy: Array.from(filters.filterTaxonomy),
    market_locale: filters.marketLocale,
    sort:          filters.sortBy,
    cursor:        opts.cursor ?? undefined,
    limit:         opts.limit ?? MARKETPLACE_SEARCH_PAGE_SIZE,
  };
}

/**
 * A dependency-array-friendly key for "did the effective search change" —
 * Set-typed filters.categories doesn't have stable reference equality
 * across parent re-renders even with identical members, so this flattens
 * it to a sorted, joined string. Excludes cursor/limit (position, not the
 * search itself) — same exclusion rule as the backend's own search key
 * (engine/supply_integration/marketplace_search_cache.py).
 */
export function marketplaceFiltersKey(filters: MarketplaceFiltersLike): string {
  return [
    Array.from(filters.categories).sort().join("|"),
    filters.duration ?? "",
    filters.timeOfDay ?? "",
    filters.maxPrice ?? "",
    filters.includeTipBased ? "1" : "0",
    filters.providerOnly ? "1" : "0",
    filters.reservable ? "1" : "0",
    filters.sortBy,
    Array.from(filters.providers).sort().join("|"),
    filters.serviceTypeView,
    Array.from(filters.filterTaxonomy).sort().join("|"),
    filters.marketLocale,
  ].join("::");
}
